import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, Leaf } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

export default function GPLetter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  useEffect(() => {
    const raw = localStorage.getItem("tp_plan");
    if (raw) setPlan(JSON.parse(raw));
    else navigate("/app/taper");
  }, [navigate]);

  if (!plan) return null;

  return (
    <div className="mx-auto max-w-3xl">
      <div className="no-print mb-6 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate("/app/taper")} data-testid="back-btn"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
        <Button data-testid="print-btn" onClick={() => window.print()} className="rounded-full bg-[#4A7c59] hover:bg-[#3B6347]"><Printer className="mr-2 h-4 w-4" /> Print / Save as PDF</Button>
      </div>

      <div className="rounded-2xl border border-border bg-white p-8 shadow-sm sm:p-12" id="letter">
        <div className="mb-8 flex items-center gap-2 border-b border-border pb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#4A7c59] text-white"><Leaf className="h-5 w-5" /></div>
          <div><div className="font-head text-xl font-bold text-[#2C363F]">TaperPath</div><div className="text-xs text-muted-foreground">Benzodiazepine taper discussion summary</div></div>
        </div>

        <p className="text-sm text-muted-foreground">Date: {today}</p>
        <h1 className="mt-4 font-head text-2xl font-bold text-[#2C363F]">Dear Doctor / Prescriber,</h1>
        <p className="mt-4 leading-relaxed text-[#2C363F]">I am using the TaperPath harm-reduction tool to help me reduce my benzodiazepine use safely. Below is a reference summary based on The Ashton Manual (benzo.org.uk/manual) that I would like to discuss with you. I understand this is educational information only and that any taper should be agreed and supervised by you.</p>

        <div className="mt-6 rounded-xl bg-muted p-5">
          <h3 className="font-head font-semibold text-[#2C363F]">Current situation</h3>
          <table className="mt-3 w-full text-sm">
            <tbody>
              <tr><td className="py-1 text-muted-foreground">Patient reference</td><td className="py-1 font-medium">{user?.display_name}</td></tr>
              <tr><td className="py-1 text-muted-foreground">Current medication</td><td className="py-1 font-medium">{plan.benzo.name}</td></tr>
              <tr><td className="py-1 text-muted-foreground">Current daily dose</td><td className="py-1 font-medium">{plan.dose} mg</td></tr>
              <tr><td className="py-1 text-muted-foreground">Diazepam equivalent</td><td className="py-1 font-medium">{plan.diazepam_equivalent} mg</td></tr>
              <tr><td className="py-1 text-muted-foreground">Reference taper length</td><td className="py-1 font-medium">approximately {plan.estimated_weeks} weeks</td></tr>
            </tbody>
          </table>
        </div>

        <h3 className="mt-6 font-head font-semibold text-[#2C363F]">Reference taper schedule (for discussion)</h3>
        <table className="mt-2 w-full border-collapse text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="py-2">Step</th><th className="py-2">Timing</th><th className="py-2">Diazepam eq. (from → to)</th></tr></thead>
          <tbody>{plan.schedule.map((s) => (
            <tr key={s.step} className="border-b border-border/60"><td className="py-2">{s.step}</td><td className="py-2">{s.weeks}</td><td className="py-2">{s.from_mg} → {s.to_mg} mg</td></tr>
          ))}</tbody>
        </table>

        <div className="mt-6 rounded-xl border border-[#E8907E]/40 bg-[#E8907E]/10 p-4 text-sm text-[#8a4436]">
          <strong>Disclaimer:</strong> This is educational information, not medical advice, and is derived from The Ashton Manual. It is a starting point for discussion, not a prescription. Benzodiazepines should never be stopped abruptly.
        </div>

        <p className="mt-6 leading-relaxed text-[#2C363F]">Thank you for your time and support.</p>
        <p className="mt-4 text-sm text-muted-foreground">Source: The Ashton Manual — benzo.org.uk/manual</p>
      </div>
    </div>
  );
}
